import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Rocket, X, ChevronRight } from "lucide-react";
import api from "../../api";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { useLang } from "../../context/LanguageContext";

const DISMISS_KEY = "lp-onboarding-dismissed";
const SHARED_KEY = "lp-onboarding-shared";

export const OnboardingChecklist = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const { toast } = useToast();
  const [dealCount, setDealCount] = useState(null);
  const uid = user?.id || "anon";
  const sharedKey = `${SHARED_KEY}-${uid}`;
  const dismissKey = `${DISMISS_KEY}-${uid}`;
  const [shared, setShared] = useState(() => localStorage.getItem(`${SHARED_KEY}-${uid}`) === "1");
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(`${DISMISS_KEY}-${uid}`) === "1");

  const isBusiness = user && user.role !== "shopper" && user.role !== "admin";

  useEffect(() => {
    if (!isBusiness) return;
    api.get("/my/deals").then((r) => setDealCount((r.data || []).length)).catch(() => setDealCount(0));
  }, [isBusiness]);

  if (!isBusiness || dismissed || dealCount === null) return null;

  const profileUrl = `${window.location.origin}/business/${encodeURIComponent(user.businessName || "")}`;

  const shareProfile = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      localStorage.setItem(sharedKey, "1");
      setShared(true);
      toast({ title: t("obc.profileLinkCopied"), description: t("obc.pasteItOnWhatsapp") });
    } catch {
      toast({ title: t("obc.hereSYourLink"), description: profileUrl });
    }
  };

  const steps = [
    { key: "logo", label: t("obc.addYourLogo"), hint: "Shoppers trust a deal with a face on it", done: !!user.logo, to: "/account" },
    { key: "contact", label: t("obc.addAContactNumber"), hint: "Let shoppers call or WhatsApp you", done: !!(user.contacts?.length || user.phone), to: "/account" },
    { key: "pin", label: t("obc.pinYourLocation"), hint: "Puts you on the LyteMap", done: !!(user.branches?.length), to: "/account" },
    { key: "deal", label: t("obc.postYourFirstDeal"), hint: "This is what shoppers come for", done: dealCount > 0, to: "/post-deal" },
    { key: "share", label: t("obc.shareYourProfileLink"), hint: "Your first customers come from your own followers", done: shared, action: shareProfile },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const complete = doneCount === steps.length;

  return (
    <div className="rounded-2xl border border-[#16a34a]/30 bg-card p-5 sm:p-6" data-testid="onboarding-checklist">
      <div className="flex items-start gap-3">
        <span className="h-11 w-11 rounded-xl bg-[#16a34a] text-white flex items-center justify-center shrink-0"><Rocket size={20} /></span>
        <div className="min-w-0 flex-1">
          <h3 className="font-extrabold text-foreground">{complete ? "You're all set up 🎉" : "Get launch-ready"}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {complete ? "Every setup step is done. Keep posting — fresh deals get the most views." : `${doneCount} of ${steps.length} done — finish these and your deals get seen by far more shoppers.`}
          </p>
        </div>
        {complete && (
          <button onClick={() => { setDismissed(true); localStorage.setItem(dismissKey, "1"); }} data-testid="onboarding-dismiss" className="h-9 w-9 rounded-xl border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors shrink-0" aria-label={t("obc.dismissChecklist")}><X size={16} /></button>
        )}
      </div>

      <div className="mt-4 h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full bg-[#16a34a] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress" />
      </div>

      <ul className="mt-4 space-y-1.5">
        {steps.map((s) => {
          const Icon = s.done ? CheckCircle2 : Circle;
          const inner = (
            <>
              <Icon size={18} className={`shrink-0 ${s.done ? "text-[#16a34a]" : "text-muted-foreground"}`} />
              <span className="min-w-0 flex-1">
                <span className={`text-sm font-semibold block ${s.done ? "text-muted-foreground line-through" : "text-foreground"}`}>{s.label}</span>
                {!s.done && <span className="text-xs text-muted-foreground block">{s.hint}</span>}
              </span>
              {!s.done && <ChevronRight size={16} className="text-[#16a34a] shrink-0" />}
            </>
          );
          const cls = "w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-secondary/60";
          return (
            <li key={s.key}>
              {s.done ? (
                <div className={cls} data-testid={`onboarding-step-${s.key}`}>{inner}</div>
              ) : s.action ? (
                <button type="button" onClick={s.action} className={cls} data-testid={`onboarding-step-${s.key}`}>{inner}</button>
              ) : (
                <Link to={s.to} className={cls} data-testid={`onboarding-step-${s.key}`}>{inner}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default OnboardingChecklist;
